import type { Guide } from "../types";

// ── Apps: Business / Content / Hotel ──────────────────────────────────────
// One guide per app in these four store groups. Every app follows the same
// shape as the "add an app" overview — open the store, pick the app, configure
// ITS specific settings, save & name it, then assign it to a screen. Only the
// configure step differs per app. The assign step always points at the shared
// "Assign content to a screen" guide.

export const appsMoreGuides: Guide[] = [
  // ── Business ───────────────────────────────────────────────────────────────
  {
    slug: "meeting-room-display",
    category: "apps",
    route: "/apps/meeting-room-display",
    title: "Meeting Room Display",
    summary: "Show OCCUPIED / VACANT, the current meeting and what’s next on a door screen.",
    intro:
      "The Meeting Room Display turns a small screen outside a room into a live status board — OCCUPIED or VACANT right now, the current meeting, and the upcoming schedule. The app itself holds only the look; the room and its bookings come from Event Management, so the screen must be linked to the room’s Location.",
    prerequisites: [
      "A Location for the room in Modules → Event Management, with this screen chosen under Screen assignment.",
      "Events for that Location — added in Calendar Events, or synced from Calendar for Google / Outlook Calendar.",
    ],
    readingTime: 3,
    status: "draft",
    group: "Business",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar and open the App Store tab. Pick the Business category (or search) and find Meeting Room Display.",
          },
        ],
        screenshot: {
          src: "apps/meeting-room-display/01.png",
          alt: "The app store with Meeting Room Display in the Business group",
          todo: "Apps store, Business group, Meeting Room Display card highlighted.",
        },
      },
      {
        title: "Open the editor",
        blocks: [
          {
            type: "p",
            text: "Click the card to open Create Meeting Room Display. The preview on the right has Occupied / Vacant and Landscape / Portrait toggles. Under Preview content, keep Sample data or pick one of your Locations to preview its real schedule.",
          },
        ],
        screenshot: {
          src: "apps/meeting-room-display/02.png",
          alt: "The Create Meeting Room Display editor with a live preview",
          todo: "Meeting Room Display editor open, Preview content set to a real Location (Atlas Room · Main Building), live preview visible. Captured on the local demo with brand name ACME / RETAIL.",
        },
      },
      {
        title: "Style the occupied and vacant states",
        blocks: [
          {
            type: "p",
            text: "Set the Display name and Font. Under Branding add a Logo, Brand name and Sub-text. The Occupied state and Vacant state sections each have a Status label (for example “OCCUPIED” / “VACANT”), Background color, Text color, Status text size and an optional Background image.",
          },
          {
            type: "p",
            text: "Under Behaviour set the Upcoming page duration and Max upcoming rows, and choose whether to Show organizer name, Show upcoming meetings list, and Allow touch / scroll to page through meetings.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Theme preset saves your fonts, colours, backgrounds and logo position as a reusable theme (not the logo image). Themes are stored in your browser.",
          },
        ],
        screenshot: {
          src: "apps/meeting-room-display/03.png",
          alt: "Occupied state, Vacant state and Behaviour settings in the Meeting Room Display editor",
          caption: "Style each state, then choose how the upcoming list behaves.",
          todo: "Meeting Room Display editor scrolled to Occupied state / Vacant state, preview toggled to Vacant on Sample data (the Occupied/Vacant toggle only applies to sample data). Sample organiser names replaced with demo people for the capture.",
        },
      },
      {
        title: "Create it and assign it to the door screen",
        blocks: [
          {
            type: "p",
            text: "Click Create. The app is saved to My Apps. Assign it to the screen by the room’s door — the same screen you chose under Screen assignment on the room’s Location, so it knows which room to show.",
          },
          {
            type: "steps-ref",
            label: "See: Event Management — Locations and events",
            to: "/modules/event-management",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "Event Management", to: "/modules/event-management" },
      { label: "Event Board", to: "/apps/event-board" },
      { label: "Calendar for Google", to: "/apps/calendar-for-google" },
    ],
    keywords: [
      "meeting room",
      "room booking",
      "occupied",
      "vacant",
      "door sign",
      "free busy",
      "calendar",
      "location",
    ],
  },

  {
    slug: "event-board",
    category: "apps",
    route: "/apps/event-board",
    title: "Event Board",
    summary: "List upcoming events across several rooms, with direction arrows, on a lobby screen.",
    intro:
      "The Event Board shows the events coming up across every room linked to its screen — time, event, organizer and room, with an optional direction arrow — ideal for a lobby or entrance screen. Like the Meeting Room Display, its events come from Event Management; the app holds the layout and look.",
    prerequisites: [
      "One or more Locations in Modules → Event Management, each with this screen chosen under Screen assignment.",
      "Events for those Locations — added in Calendar Events, or synced from Calendar for Google / Outlook Calendar.",
    ],
    readingTime: 3,
    status: "draft",
    group: "Business",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find Event Board in the Business category.",
          },
        ],
        screenshot: {
          src: "apps/event-board/01.png",
          alt: "The app store with Event Board in the Business group",
          todo: "Apps store, Business group, Event Board card highlighted.",
        },
      },
      {
        title: "Open the editor",
        blocks: [
          {
            type: "p",
            text: "Click the card to open Create Event Board. The preview uses sample data — on a real screen the board pulls events from the rooms assigned to that screen.",
          },
        ],
        screenshot: {
          src: "apps/event-board/02.png",
          alt: "The Create Event Board editor with a live preview",
          todo: "Event Board editor open, live preview visible (sample data; the editor has no room picker). Sample organiser names replaced with demo people for the capture.",
        },
      },
      {
        title: "Set the header, look and behaviour",
        blocks: [
          {
            type: "p",
            text: "Under Header set the Title and Subtitle, a Logo, Brand name and Brand sub-text. Under Background choose Default (animated), Solid color or Background image, and set the Text colors.",
          },
          {
            type: "p",
            text: "Under Behaviour set Days ahead, Page duration and Max rows per page, then pick what each row shows: Show organizer column, Show direction arrows, Show room icon, Show room location label, Show event / room counters, and Allow touch / scroll paging.",
          },
          {
            type: "note",
            tone: "info",
            text: "Direction arrows and room icons come from each Location’s Wayfinding settings in Event Management.",
          },
        ],
        screenshot: {
          src: "apps/event-board/03.png",
          alt: "Header, Background, Text colors and Behaviour settings in the Event Board editor",
          caption: "Brand the header, then choose what each row shows.",
          todo: "Event Board editor scrolled to Background / Text colors / Behaviour sections with the checkboxes visible.",
        },
      },
      {
        title: "Create it and assign it to a screen",
        blocks: [
          {
            type: "p",
            text: "Click Create to save it to My Apps, then assign it to your lobby or entrance screen. Link that screen to each room it should list under Screen assignment on the rooms’ Locations.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "Event Management", to: "/modules/event-management" },
      { label: "Meeting Room Display", to: "/apps/meeting-room-display" },
    ],
    keywords: [
      "event board",
      "events",
      "sessions",
      "agenda",
      "schedule",
      "conference",
      "direction arrows",
      "lobby",
    ],
  },

  {
    slug: "queue-now-serving",
    category: "apps",
    route: "/apps/queue-now-serving",
    title: "Queue / Now Serving",
    summary: "Show the ticket being called, the counters serving and who’s next.",
    intro:
      "The Queue / Now Serving app is the waiting-room board of the Queue module: it shows the ticket being called, the counters currently serving, and the next numbers, with a chime or a spoken announcement on each call.",
    prerequisites: [
      "The Queue module on your organisation. Without it, the Queue apps don’t appear in the store.",
      "A queue with its services and counters, set up in the operator app at queue.pulsepanels.com.",
    ],
    readingTime: 3,
    status: "draft",
    group: "Business",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find Queue / Now Serving in the Business category.",
          },
          {
            type: "note",
            tone: "info",
            text: "Queue is a paid module. If you don’t see the card, ask your PulsePanels partner to enable the Queue module for your organisation.",
          },
        ],
        screenshot: {
          src: "apps/queue-now-serving/01.png",
          alt: "The app store with Queue / Now Serving in the Business group",
          todo: "Apps store, Business group, Queue / Now Serving card highlighted.",
        },
      },
      {
        title: "Choose the queue",
        blocks: [
          {
            type: "p",
            text: "The editor opens as Queue Display. Set the App name, then pick the Queue this screen shows. Services, counters and live operations are managed in the operator app at queue.pulsepanels.com — if you have no queues yet, the editor links you there.",
          },
        ],
        screenshot: {
          src: "apps/queue-now-serving/02.png",
          alt: "The Queue Display editor with the Queue picker and a live preview",
          todo: "Queue Display editor open: App name, Queue picker, Display title, preview visible.",
        },
      },
      {
        title: "Set the board, appearance and calling voice",
        blocks: [
          {
            type: "p",
            text: "Set the Display title, Orientation, Now Serving rows (how many counters are shown) and Next-up tickets shown. Keep Play chime on new call on for an audible cue.",
          },
          {
            type: "p",
            text: "Under Appearance set the Header text, Background (Default gradient, Solid colour or Uploaded image) and the Primary, Accent and Text colours. Under Calling voice, turn on Announce each call with a spoken voice to read out the ticket and counter, or upload a Custom call sound (MP3) to replace the chime. Choose the Default language — English, Arabic, French, or Bilingual (English + Arabic).",
          },
        ],
        screenshot: {
          src: "apps/queue-now-serving/03.png",
          alt: "Now Serving rows, chime, Appearance and Calling voice settings",
          caption: "Size the board, brand it, and choose chime or voice.",
          todo: "Queue Display editor scrolled to Now Serving rows / Next-up tickets shown / Appearance / Calling voice sections.",
        },
      },
      {
        title: "Save and assign to the waiting-area screen",
        blocks: [
          {
            type: "p",
            text: "Click Save changes, then assign the app to the screen your visitors can see.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "Queue Management", to: "/modules/queue-management" },
      { label: "Queue Kiosk", to: "/apps/queue-kiosk" },
    ],
    keywords: [
      "queue",
      "now serving",
      "ticket",
      "counter",
      "waiting",
      "chime",
      "voice announcement",
      "call number",
    ],
  },

  {
    slug: "queue-kiosk",
    category: "apps",
    route: "/apps/queue-kiosk",
    title: "Queue Kiosk",
    summary: "Let visitors pick a service and take a ticket on a touch screen.",
    intro:
      "The Queue Kiosk runs on a touch screen: visitors pick a service, tap Get my ticket, and receive a number — printed, and shown on screen with a QR code to track their place. It feeds the same queue your Now Serving boards display.",
    prerequisites: [
      "The Queue module on your organisation. Without it, the Queue apps don’t appear in the store.",
      "A queue with its services, set up in the operator app at queue.pulsepanels.com.",
    ],
    readingTime: 3,
    status: "draft",
    group: "Business",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find Queue Kiosk in the Business category.",
          },
        ],
        screenshot: {
          src: "apps/queue-kiosk/01.png",
          alt: "The app store with Queue Kiosk in the Business group",
          todo: "Apps store, Business group, Queue Kiosk card highlighted.",
        },
      },
      {
        title: "Choose the queue",
        blocks: [
          {
            type: "p",
            text: "The Queue Kiosk editor opens with a live preview of the touch screen. Set the App name and pick the Queue. The service buttons on the kiosk are the services of that queue — you add and rename them in the operator app at queue.pulsepanels.com, not here.",
          },
        ],
        screenshot: {
          src: "apps/queue-kiosk/02.png",
          alt: "The Queue Kiosk editor with a live preview of the service buttons",
          todo: "Queue Kiosk editor open, Queue picker + preview visible.",
        },
      },
      {
        title: "Set printing, reset and appearance",
        blocks: [
          {
            type: "p",
            text: "Keep Print ticket automatically on if the kiosk has a ticket printer. Idle reset (seconds) returns the kiosk to its start screen after it’s left alone. Under Appearance set the Header text, Background and colours, and choose the Default language (English, Arabic or French — visitors can switch on the kiosk).",
          },
          {
            type: "note",
            tone: "tip",
            text: "Keep service names short and plain in the operator app (“Reception”, “Billing”) so first-time visitors choose the right one quickly.",
          },
        ],
        screenshot: {
          src: "apps/queue-kiosk/03.png",
          alt: "Print ticket automatically, Idle reset and Appearance settings for the kiosk",
          caption: "Turn on printing, set the idle reset, and brand the kiosk.",
          todo: "Queue Kiosk editor scrolled to Print ticket automatically / Idle reset / Appearance / Default language.",
        },
      },
      {
        title: "Save and assign to the kiosk screen",
        blocks: [
          {
            type: "p",
            text: "Click Save changes, then assign the app to your touch-screen kiosk.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "Queue Management", to: "/modules/queue-management" },
      { label: "Queue / Now Serving", to: "/apps/queue-now-serving" },
    ],
    keywords: [
      "queue",
      "kiosk",
      "ticket",
      "take a number",
      "touch",
      "self service",
      "ticket printer",
      "qr",
    ],
  },

  {
    slug: "google-slides",
    category: "apps",
    route: "/apps/google-slides",
    title: "Google Slides",
    summary: "Play a published Google Slides deck.",
    intro:
      "The Google Slides app plays a deck you’ve published to the web and advances through it automatically. Edits you make in Google Slides show up on screen on their own.",
    readingTime: 2,
    status: "draft",
    group: "Business",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find Google Slides in the Business category.",
          },
        ],
        screenshot: {
          src: "apps/google-slides/01.png",
          alt: "The app store with Google Slides in the Business group",
          todo: "Apps store, Business group, Google Slides card highlighted.",
        },
      },
      {
        title: "Paste the published Slides link",
        blocks: [
          {
            type: "p",
            text: "In Google Slides choose File → Share → Publish to web, click Publish and copy the Link. Paste it into Published Slides link — it looks like …/presentation/d/e/2PACX-…/pub. The editor confirms “Connected · published presentation ✓”.",
          },
          {
            type: "note",
            tone: "info",
            text: "A normal edit or share link won’t load every slide on a screen — it must be the Publish to web link.",
          },
        ],
        screenshot: {
          src: "apps/google-slides/02.png",
          alt: "The Google Slides editor with the Published Slides link field",
          todo: "Google Slides editor open, link field + Publish to web help box.",
        },
      },
      {
        title: "Choose playback",
        blocks: [
          {
            type: "p",
            text: "Set Auto-advance (Play automatically, or Show first slide only), Loop (Loop back to start, or Stop at last slide) and Seconds per slide. Leave Control bar on Hidden (clean) for signage.",
          },
          {
            type: "note",
            tone: "warning",
            text: "With the Control bar Visible, a tap on a touch screen opens Google’s menu and can stop the slideshow.",
          },
        ],
        screenshot: {
          src: "apps/google-slides/03.png",
          alt: "Auto-advance, Loop, Seconds per slide and Control bar settings",
          caption: "Set auto-advance, looping and the time per slide. The preview shows the deck once a published link is pasted.",
          todo: "Google Slides editor: Auto-advance, Loop, Seconds per slide, Control bar highlighted. Captured without a published deck (no neutral published deck available), so the preview is empty. Recapture with a neutral published deck if one is made.",
        },
      },
      {
        title: "Save and assign to a screen",
        blocks: [
          {
            type: "p",
            text: "Give the app a name, click Save Changes, and assign it to a screen.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "Google Sheets", to: "/apps/google-sheets" },
      { label: "Power BI", to: "/apps/power-bi" },
    ],
    keywords: [
      "google slides",
      "slides",
      "presentation",
      "deck",
      "published",
      "publish to web",
    ],
  },

  {
    slug: "google-sheets",
    category: "apps",
    route: "/apps/google-sheets",
    title: "Google Sheets",
    summary: "Display a Google Sheet as a styled table that refreshes itself.",
    intro:
      "The Google Sheets app shows data from a sheet — a price list, a leaderboard, a rota — as a clean table, and re-reads it on a schedule so the screen reflects your latest edits.",
    readingTime: 2,
    status: "draft",
    group: "Business",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find Google Sheets in the Business category.",
          },
        ],
        screenshot: {
          src: "apps/google-sheets/01.png",
          alt: "The app store with Google Sheets in the Business group",
          todo: "Apps store, Business group, Google Sheets card highlighted.",
        },
      },
      {
        title: "Paste the sheet’s share link",
        blocks: [
          {
            type: "p",
            text: "In Google Sheets click Share and set General access to “Anyone with the link → Viewer”, then copy the link and paste it into Google Sheet link. Optionally set the Tab name and a Cell range (for example A1:D20) to show just part of the sheet.",
          },
          {
            type: "note",
            tone: "info",
            text: "The sheet must be shared “Anyone with the link → Viewer”. A private sheet can’t be read by the screen.",
          },
        ],
        screenshot: {
          src: "apps/google-sheets/02.png",
          alt: "The Google Sheets editor with the sheet link, tab name and cell range fields",
          todo: "Google Sheets editor open, link + Tab name + Cell range fields.",
        },
      },
      {
        title: "Style the table and set the refresh",
        blocks: [
          {
            type: "p",
            text: "Choose whether the First row is a header row, turn on Row striping, add an optional Title, and set Refresh (every minute up to every hour). Pick a Font, Font size, Background, Header color, Text color and Background opacity.",
          },
        ],
        screenshot: {
          src: "apps/google-sheets/03.png",
          alt: "First row, Row striping, Refresh and styling settings",
          caption: "Style the table and choose how often it refreshes. The preview shows the table once a shared link is pasted.",
          todo: "Google Sheets editor scrolled to First row, Row striping, Refresh and colours. Captured without a shared sheet, so the preview is empty. Recapture with a neutral shared sheet if one is made.",
        },
      },
      {
        title: "Save and assign to a screen",
        blocks: [
          {
            type: "p",
            text: "Name the app, click Save Changes, and assign it to a screen.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "Google Slides", to: "/apps/google-slides" },
    ],
    keywords: [
      "google sheets",
      "spreadsheet",
      "data",
      "table",
      "range",
      "refresh",
      "anyone with the link",
    ],
  },

  {
    slug: "calendar-for-google",
    category: "apps",
    route: "/apps/calendar-for-google",
    title: "Calendar for Google",
    summary: "Sync a Google Calendar into your meeting rooms.",
    intro:
      "Calendar for Google isn’t a display on its own — it connects your Google account (read-only) and maps a Google Calendar to a meeting-room Location. Its events then appear on that room’s Meeting Room Display and on Event Boards automatically.",
    prerequisites: [
      "A Location for the room in Modules → Event Management.",
      "A Google account that can see the room’s calendar.",
    ],
    readingTime: 3,
    status: "draft",
    group: "Business",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open Calendar for Google from the store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and click Calendar for Google in the Business category. It opens the Google Calendar integration page.",
          },
        ],
        screenshot: {
          src: "apps/calendar-for-google/01.png",
          alt: "The app store with Calendar for Google in the Business group",
          todo: "Apps store, Business group, Calendar for Google card highlighted.",
        },
      },
      {
        title: "Connect your Google account",
        blocks: [
          {
            type: "p",
            text: "Click Connect Google Calendar and sign in with Google. PulsePanels gets read-only access to calendar events. The account then appears under Connected Accounts.",
          },
        ],
        screenshot: {
          src: "apps/calendar-for-google/02.png",
          alt: "The Google Calendar integration page with the Connect Google Calendar button",
          todo: "Google Calendar integration page before any account is connected: Connect Google Calendar card and the \"No Google Calendar accounts connected yet\" note.",
        },
      },
      {
        title: "Map a calendar to a room and sync",
        blocks: [
          {
            type: "p",
            text: "On the connected account, use Map a Calendar: search for the calendar, then choose its Meeting Room Location, how far ahead to Fetch events up to, and a Source priority. The mapping appears under Active Calendar Sources — click Sync on a source, or Sync All, to pull events now.",
          },
          {
            type: "note",
            tone: "info",
            text: "Private events show as “Private Meeting” — their title and description are never shown. Resolve Conflicts cancels overlapping events from lower-priority sources.",
          },
        ],
        screenshot: {
          src: "apps/calendar-for-google/03.png",
          alt: "The How it works steps: connect, map a calendar to a room, sync",
          caption: "Map a Calendar and Active Calendar Sources appear once an account is connected.",
          todo: "Google Calendar integration page with How it works highlighted (no account connected on the demo). DEFERRED: Map a Calendar + Active Calendar Sources need a connected Google account.",
        },
      },
      {
        title: "Show it on screen",
        blocks: [
          {
            type: "p",
            text: "Put a Meeting Room Display (one room, OCCUPIED / VACANT) or an Event Board (several rooms) on the screens linked to that Location. There’s nothing to save in My Apps for the calendar itself.",
          },
          {
            type: "steps-ref",
            label: "See: Meeting Room Display",
            to: "/apps/meeting-room-display",
          },
        ],
      },
    ],
    related: [
      { label: "Event Management", to: "/modules/event-management" },
      { label: "Meeting Room Display", to: "/apps/meeting-room-display" },
      { label: "Event Board", to: "/apps/event-board" },
      { label: "Outlook Calendar", to: "/apps/outlook-calendar" },
    ],
    keywords: [
      "google calendar",
      "calendar",
      "sync",
      "map a calendar",
      "meeting room",
      "location",
      "authorize",
    ],
  },

  {
    slug: "outlook-calendar",
    category: "apps",
    route: "/apps/outlook-calendar",
    title: "Outlook Calendar",
    summary: "Sync an Outlook / Microsoft 365 calendar into your meeting rooms.",
    intro:
      "Outlook Calendar connects your Microsoft / Outlook account (read-only) and maps an Outlook calendar to a meeting-room Location. Events are synced through Microsoft Graph and appear on that room’s Meeting Room Display and on Event Boards automatically.",
    prerequisites: [
      "A Location for the room in Modules → Event Management.",
      "A Microsoft 365 / Outlook account that can see the room’s calendar.",
    ],
    readingTime: 3,
    status: "draft",
    group: "Business",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open Outlook Calendar from the store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and click Outlook Calendar in the Business category. It opens the Outlook Calendar integration page.",
          },
        ],
        screenshot: {
          src: "apps/outlook-calendar/01.png",
          alt: "The app store with Outlook Calendar in the Business group",
          todo: "Apps store, Business group, Outlook Calendar card highlighted.",
        },
      },
      {
        title: "Connect your Microsoft account",
        blocks: [
          {
            type: "p",
            text: "Click Connect Outlook Calendar and sign in with Microsoft, granting read-only access to calendar events. The account then appears under Connected Accounts.",
          },
        ],
        screenshot: {
          src: "apps/outlook-calendar/02.png",
          alt: "The Outlook Calendar integration page with Connect Outlook Calendar and How it works",
          todo: "Outlook Calendar integration page before any account is connected: Connect Outlook Calendar card + How it works.",
        },
      },
      {
        title: "Map a calendar to a room and sync",
        blocks: [
          {
            type: "p",
            text: "On the connected account, use Map a Calendar to pick the calendar and its Meeting Room Location, how far ahead to Fetch events up to, and a Source priority. Then click Sync (or Sync All) to pull the events.",
          },
          {
            type: "note",
            tone: "info",
            text: "Private events show as “Private Meeting” — their title and description are never shown.",
          },
        ],
        screenshot: {
          src: "apps/outlook-calendar/03.png",
          alt: "The How it works steps: connect, map a calendar to a room, sync",
          caption: "Map a Calendar and Active Calendar Sources appear once an account is connected.",
          todo: "Outlook Calendar integration page with How it works highlighted (no account connected on the demo). DEFERRED: Map a Calendar + Active Calendar Sources need a connected Microsoft account.",
        },
      },
      {
        title: "Show it on screen",
        blocks: [
          {
            type: "p",
            text: "Put a Meeting Room Display or an Event Board on the screens linked to that Location — they pick up the synced events on their own.",
          },
          {
            type: "steps-ref",
            label: "See: Meeting Room Display",
            to: "/apps/meeting-room-display",
          },
        ],
      },
    ],
    related: [
      { label: "Event Management", to: "/modules/event-management" },
      { label: "Meeting Room Display", to: "/apps/meeting-room-display" },
      { label: "Event Board", to: "/apps/event-board" },
      { label: "Calendar for Google", to: "/apps/calendar-for-google" },
    ],
    keywords: [
      "outlook",
      "microsoft 365",
      "office 365",
      "calendar",
      "sync",
      "map a calendar",
      "meeting room",
    ],
  },

  {
    slug: "building-directory",
    category: "apps",
    route: "/apps/building-directory",
    title: "Building Directory",
    summary: "A tenant / office directory board, by floor, in English, Arabic or both.",
    intro:
      "The Building Directory lists the tenants or offices in a building floor by floor — name, logo, unit and an optional direction arrow — in English, Arabic (right-to-left) or both. Long lists page automatically, and the editor shows a live preview as you type.",
    readingTime: 3,
    status: "draft",
    group: "Business",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find Building Directory in the Business category.",
          },
        ],
        screenshot: {
          src: "apps/building-directory/01.png",
          alt: "The app store with Building Directory in the Business group",
          todo: "Apps store, Business group, Building Directory card highlighted.",
        },
      },
      {
        title: "Set the heading and language",
        blocks: [
          {
            type: "p",
            text: "Set the App name, Heading and optional Subtitle, add a Building logo, and an optional Map link (shown as a “scan to open map” QR code in the header). Choose the Display language: English only, Arabic only (RTL), or Bilingual (English + Arabic) — the Arabic heading fields appear when needed.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Already have a directory screen? Inspire from an image reads the colours, text and arrows from a photo of it into the app — review the result before saving.",
          },
        ],
        screenshot: {
          src: "apps/building-directory/02.png",
          alt: "The Building Directory editor with Heading, Building logo and Map link, and a live preview",
          todo: "Building Directory editor open, heading fields + live preview visible.",
        },
      },
      {
        title: "Add floors and entries, then lay it out",
        blocks: [
          {
            type: "p",
            text: "Under Floors & entries, use + Add floor and + Add entry. Each entry has a name, an optional logo and a direction arrow. Choose the Columns, the Entry logos size and a Base font, then the Background, Accent color and Font color below the entries.",
          },
          {
            type: "p",
            text: "Tick Show advanced options for more: Group entries by (Floor, Category, or Alphabetical), the Index bar, Rows per column and Page duration so a long directory pages through instead of shrinking, a category and status (Open, Closed, Coming soon or Relocated) on each entry, per-element fonts and sizes, and Theme & layout — quick-start templates and reusable themes.",
          },
        ],
        screenshot: {
          src: "apps/building-directory/03.png",
          alt: "Floors & entries list with direction arrows, grouping and paging settings",
          caption: "Add floors and entries, then set grouping and paging.",
          todo: "Building Directory editor scrolled to Floors & entries (an entry with direction + status visible) and the Group entries by / Rows per column / Page duration controls.",
        },
      },
      {
        title: "Save and assign to a screen",
        blocks: [
          {
            type: "p",
            text: "Name the directory, click Save Changes, and assign it to your lobby directory screen.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "Campus Directory", to: "/apps/campus-directory" },
      { label: "Hotel Directory", to: "/apps/hotel-directory" },
    ],
    keywords: [
      "building directory",
      "tenant",
      "office",
      "directory",
      "floor",
      "arabic",
      "bilingual",
      "rtl",
      "paging",
    ],
  },

  {
    slug: "campus-directory",
    category: "apps",
    route: "/apps/campus-directory",
    title: "Campus Directory",
    summary: "An interactive directory for campuses and large sites — buildings, rooms by floor, quick access.",
    intro:
      "The Campus Directory is a touch-friendly directory for larger sites: a list of buildings, a by-floor room guide, quick-access shortcuts, search and filters, an accessibility mode, and landscape or portrait layouts.",
    prerequisites: [
      "The Wayfinding module on your organisation. Without it, Campus Directory doesn’t appear in the store.",
    ],
    readingTime: 3,
    status: "draft",
    group: "Business",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find Campus Directory in the Business category.",
          },
          {
            type: "note",
            tone: "info",
            text: "Campus Directory is part of the paid Wayfinding module. If you don’t see it, ask your PulsePanels partner.",
          },
        ],
        screenshot: {
          src: "apps/campus-directory/01.png",
          alt: "The app store with Campus Directory in the Business group",
          todo: "Apps store, Business group, Campus Directory card highlighted.",
        },
      },
      {
        title: "Set the header, search and accessibility",
        blocks: [
          {
            type: "p",
            text: "The editor opens with Directory type set to Campus / University. Set the Title and You are here, and the QR label and QR link shown in the header. Under Header & footer, Search & filters and Accessibility choose what visitors see — for example the Search bar and the Category, Floor, Language and Sort A–Z filters.",
          },
        ],
        screenshot: {
          src: "apps/campus-directory/02.png",
          alt: "The Directory editor set to Campus / University with a live preview",
          todo: "Directory editor (Campus / University) open, header + filters + live preview.",
        },
      },
      {
        title: "Add buildings, floors and rooms",
        blocks: [
          {
            type: "p",
            text: "Use + Add building to list the buildings, then + Add floor for the floors. Under Rooms & Places add rooms one at a time (+ Add room) or in bulk: download the CSV Template, fill it in (Name, Code, Building, Floor, Category, Description), and use Upload CSV to add new rooms or update existing ones. Add Quick Access items and Footer notices, and style the board with a logo, fonts, background and colours.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Tick Accessibility in the preview to check the accessible layout, where search and filters move to a dock at the bottom.",
          },
        ],
        screenshot: {
          src: "apps/campus-directory/03.png",
          alt: "Buildings and Rooms & Places sections with CSV upload",
          caption: "List the buildings, then add rooms by hand or by CSV.",
          todo: "Directory editor (Campus) scrolled to Buildings + Rooms & Places with Upload CSV / Template buttons.",
        },
      },
      {
        title: "Save and assign to a screen",
        blocks: [
          {
            type: "p",
            text: "Name the directory, click Save Changes, and assign it to your campus screen.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "Hotel Directory", to: "/apps/hotel-directory" },
      { label: "Building Directory", to: "/apps/building-directory" },
      { label: "Wayfinding", to: "/modules/wayfinding" },
    ],
    keywords: [
      "campus directory",
      "directory",
      "university",
      "buildings",
      "rooms",
      "csv",
      "accessibility",
      "touch",
    ],
  },

  // ── Content ────────────────────────────────────────────────────────────────
  {
    slug: "digital-signs",
    category: "apps",
    route: "/apps/digital-signs",
    title: "Digital Signs",
    summary: "Rotating quotes, facts and Qur’an verses on an animated background.",
    intro:
      "Digital Signs shows a rotating series of quotes over an animated background — pick a category, add your own lines, and set how long each stays on screen.",
    readingTime: 2,
    status: "draft",
    group: "Content",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find Digital Signs in the Content category.",
          },
        ],
        screenshot: {
          src: "apps/digital-signs/01.png",
          alt: "The app store with Digital Signs in the Content group",
          todo: "Apps store, Content group, Digital Signs card highlighted.",
        },
      },
      {
        title: "Open the editor",
        blocks: [
          {
            type: "p",
            text: "Click the card to open New Digital Signs, with a live preview on the right.",
          },
        ],
        screenshot: {
          src: "apps/digital-signs/02.png",
          alt: "The Digital Signs editor with a live preview",
          todo: "Digital Signs editor open, live preview visible.",
        },
      },
      {
        title: "Choose the quotes and the look",
        blocks: [
          {
            type: "p",
            text: "Pick a Quote category — Inspirational, Motivational, Educational, Success, Wellbeing or Qur’an (for Qur’an, also choose the Qur’an language: English, Arabic or both). Set the Quote duration in seconds and a Font.",
          },
          {
            type: "p",
            text: "For Background choose Animated theme (with a Theme style such as Space, Aurora or Minimal), Image or Solid color, and optionally a Font color. Under Your own quotes, add lines of your own — they’re shown alongside the category.",
          },
        ],
        screenshot: {
          src: "apps/digital-signs/03.png",
          alt: "Quote category, duration, background and Your own quotes settings",
          caption: "Pick a category, set the duration, choose a background.",
          todo: "Digital Signs editor with Quote category open (Qur’an selected shows Qur’an language) and a custom quote added under Your own quotes.",
        },
      },
      {
        title: "Save and assign to a screen",
        blocks: [
          {
            type: "p",
            text: "Name the app, click Save Changes, and assign it to a screen.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "PosterMyWall", to: "/apps/postermywall" },
    ],
    keywords: [
      "digital signs",
      "quotes",
      "messages",
      "inspiration",
      "quran",
      "animated background",
      "rotation",
    ],
  },

  {
    slug: "postermywall",
    category: "apps",
    route: "/apps/postermywall",
    title: "PosterMyWall",
    summary: "Embed a published PosterMyWall design that auto-updates.",
    intro:
      "The PosterMyWall app shows a design you’ve published in PosterMyWall, full-screen. When you edit and republish the design, the screen updates itself — the embed link stays the same, so there’s nothing to change in PulsePanels.",
    readingTime: 2,
    status: "draft",
    group: "Content",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find PosterMyWall in the Content category.",
          },
        ],
        screenshot: {
          src: "apps/postermywall/01.png",
          alt: "The app store with PosterMyWall in the Content group",
          todo: "Apps store, Content group, PosterMyWall card highlighted.",
        },
      },
      {
        title: "Get the embed code from PosterMyWall",
        blocks: [
          {
            type: "p",
            text: "The editor shows How to get your embed code:",
          },
          {
            type: "ol",
            items: [
              "Open your design → Resize → Digital Display (16:9 or 9:16).",
              "Click Publish → Embed in Web page.",
              "Copy the embed code.",
            ],
          },
        ],
        screenshot: {
          src: "apps/postermywall/02.png",
          alt: "The PosterMyWall editor with the embed link field and How to get your embed code",
          todo: "PosterMyWall editor open, embed link field + the three how-to cards.",
        },
      },
      {
        title: "Paste it and choose the fit",
        blocks: [
          {
            type: "p",
            text: "Paste the iframe link (or the full embed code) into PosterMyWall embed link — the preview loads your design. Choose the Fit: Fill screen (crop if needed) or Fit whole design (letterbox), and a Background color behind the design.",
          },
          {
            type: "note",
            tone: "info",
            text: "Use the embed code — not a downloaded image — so the screen updates when you republish.",
          },
        ],
        screenshot: {
          src: "apps/postermywall/03.png",
          alt: "The Fit and Background color settings under the embed link field",
          caption: "Paste the embed link, then choose the fit; it auto-updates on republish.",
          todo: "PosterMyWall editor with Fit + Background color highlighted. Captured without an embed link (no neutral published design), so the preview is empty.",
        },
      },
      {
        title: "Save and assign to a screen",
        blocks: [
          {
            type: "p",
            text: "Name the app, click Save Changes, and assign it to a screen.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "Canva", to: "/apps/canva" },
    ],
    keywords: [
      "postermywall",
      "poster",
      "design",
      "embed",
      "published",
      "auto update",
    ],
  },

  {
    slug: "youtube",
    category: "apps",
    route: "/apps/youtube",
    title: "YouTube",
    summary: "Play a queue of YouTube videos or playlists.",
    intro:
      "The YouTube app plays a queue of YouTube videos and playlists on your screen — handy for promos, showreels, or background footage — with options for sound, looping, controls and captions.",
    readingTime: 2,
    status: "draft",
    group: "Content",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find YouTube in the Content category.",
          },
        ],
        screenshot: {
          src: "apps/youtube/01.png",
          alt: "The app store with YouTube in the Content group",
          todo: "Apps store, Content group, YouTube card highlighted.",
        },
      },
      {
        title: "Open the editor",
        blocks: [
          {
            type: "p",
            text: "Click the card to open New YouTube, with a preview on the right. The preview is always muted; on screens the app follows your sound setting.",
          },
        ],
        screenshot: {
          src: "apps/youtube/02.png",
          alt: "The YouTube editor with Add to queue and Playback settings",
          todo: "YouTube editor open, Add to queue field + Playback section.",
        },
      },
      {
        title: "Build the queue and set playback",
        blocks: [
          {
            type: "p",
            text: "Paste a YouTube video or playlist URL under Add to queue and click Add; repeat for more. The Queue lists what will play, in order. Under Playback choose Sound on by default, Loop queue, Show player controls and Show captions, and set the Fit: Fit (letterbox), Fill (crop) or Stretch.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Many public screens have no speakers — untick Sound on by default for those.",
          },
        ],
        screenshot: {
          src: "apps/youtube/03.png",
          alt: "A YouTube queue with videos added and the Playback options",
          caption: "Add videos to the queue, then set sound, loop and fit.",
          todo: "YouTube editor with two or three videos in the Queue and a video playing in the preview.",
        },
      },
      {
        title: "Save and assign to a screen",
        blocks: [
          {
            type: "p",
            text: "Name the app, click Save Changes, and assign it to a screen.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "YouTube Live", to: "/apps/youtube-live" },
    ],
    keywords: [
      "youtube",
      "video",
      "playlist",
      "queue",
      "loop",
      "sound",
      "captions",
      "showreel",
    ],
  },

  {
    slug: "youtube-live",
    category: "apps",
    route: "/apps/youtube-live",
    title: "YouTube Live",
    summary: "Show a live YouTube stream — a popular 24/7 channel or your own.",
    intro:
      "YouTube Live shows an ongoing live stream on your screen — a popular 24/7 news, business or space channel, or any live video or channel you paste in.",
    readingTime: 2,
    status: "draft",
    group: "Content",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find YouTube Live in the Content category.",
          },
        ],
        screenshot: {
          src: "apps/youtube-live/01.png",
          alt: "The app store with YouTube Live in the Content group",
          todo: "Apps store, Content group, YouTube Live card highlighted.",
        },
      },
      {
        title: "Choose the source",
        blocks: [
          {
            type: "p",
            text: "In New YouTube Live, under Source pick Popular channels and choose one of the listed 24/7 channels, or pick Custom URL and paste a live video URL, a channel URL, or a channel ID (starting UC…).",
          },
        ],
        screenshot: {
          src: "apps/youtube-live/02.png",
          alt: "The YouTube Live editor with the Popular channels and Custom URL source options",
          todo: "YouTube Live editor open on Custom URL with the field still empty (the Popular channels list auto-plays a news broadcaster, and the NASA preset was unavailable).",
        },
      },
      {
        title: "Set sound, controls, fit and quality",
        blocks: [
          {
            type: "p",
            text: "Under Playback choose Sound on and Show player controls, then set the Fit (Fit (letterbox), Fill (crop) or Stretch) and the Quality — Auto (recommended), or a fixed 1080p to 360p.",
          },
          {
            type: "note",
            tone: "info",
            text: "With Sound on, the screen starts the stream muted and turns the sound on once it’s playing — some players refuse to start a stream with sound, which used to leave the screen black.",
          },
          {
            type: "note",
            tone: "tip",
            text: "A stream can be offline between broadcasts. Put this app in a playlist with other content so the screen is never left empty.",
          },
        ],
        screenshot: {
          src: "apps/youtube-live/03.png",
          alt: "A custom live stream URL previewing, with Sound on, Show player controls, Fit and Quality settings",
          caption: "Choose sound, fit and quality.",
          todo: "YouTube Live editor with a Custom URL (a 24/7 ISS Earth-view stream, no people) previewing and the Playback settings: Sound on, Show player controls, Fit, Quality.",
        },
      },
      {
        title: "Save and assign to a screen",
        blocks: [
          {
            type: "p",
            text: "Name the app, click Save Changes, and assign it to a screen.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "YouTube", to: "/apps/youtube" },
      { label: "Live Sources", to: "/apps/live-sources" },
    ],
    keywords: [
      "youtube live",
      "live stream",
      "broadcast",
      "streaming",
      "channel",
      "news channel",
      "muted",
    ],
  },

  {
    slug: "canva",
    category: "apps",
    route: "/apps/canva",
    title: "Canva",
    summary: "Export a Canva design as a video or image that plays on your screens.",
    intro:
      "The Canva app connects to your Canva account and exports a design as an MP4 video (for animated designs) or a PNG image (for static ones). The export plays full-screen without Canva branding and works offline; re-export whenever you change the design.",
    readingTime: 3,
    status: "draft",
    group: "Content",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find Canva in the Content category.",
          },
        ],
        screenshot: {
          src: "apps/canva/01.png",
          alt: "The app store with Canva in the Content group",
          todo: "Apps store, Content group, Canva card highlighted.",
        },
      },
      {
        title: "Connect Canva and paste the design URL",
        blocks: [
          {
            type: "p",
            text: "Click Connect Canva Account and sign in to Canva. Then set the App name and paste the Canva design URL — copy it from your browser’s address bar, or use Share → Copy link in Canva (canva.link/… short links work too).",
          },
        ],
        screenshot: {
          src: "apps/canva/02.png",
          alt: "The Canva editor with Connect Canva Account and the Canva design URL field",
          todo: "Canva editor open, Connect Canva Account box + design URL field.",
        },
      },
      {
        title: "Save, then export the design",
        blocks: [
          {
            type: "p",
            text: "Pick the Export format — MP4 video (animated designs) or PNG image (static designs) — and click Save Changes — a new app has to be saved once before it can be exported. The editor stays open: click Export Design; it can take up to a minute. When it says Export ready, the design plays on screen.",
          },
          {
            type: "p",
            text: "To change the design later, click Edit in Canva, make your changes in Canva, then come back and click Done & Re-export. Save to Library also adds the export to Media for use in playlists and channels (needs permission to upload media).",
          },
        ],
        screenshot: {
          src: "apps/canva/03.png",
          alt: "A Canva design URL pasted with the Export format menu and the Save first, then export hint",
          caption: "Save first, then export the design as a video or image; re-export after edits.",
          todo: "Canva editor with a placeholder design URL: Export format + \"Save first, then export\". DEFERRED: Export Design / Export ready / Save to Library / Edit in Canva need a connected Canva account and a saved app.",
        },
      },
      {
        title: "Assign it to a screen",
        blocks: [
          {
            type: "p",
            text: "Assign the saved Canva app to a screen or add it to a playlist.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "PosterMyWall", to: "/apps/postermywall" },
      { label: "Upload media", to: "/content/upload-media" },
    ],
    keywords: [
      "canva",
      "design",
      "graphic",
      "export",
      "mp4",
      "png",
      "re-export",
      "media library",
    ],
  },

  // ── Hotel ──────────────────────────────────────────────────────────────────
  {
    slug: "hotel-directory",
    category: "apps",
    route: "/apps/hotel-directory",
    title: "Hotel Directory",
    summary: "An interactive guest directory — services, facilities, floor guide and more.",
    intro:
      "The Hotel Directory is a premium, touch-friendly guest directory: Guest Services, Facilities with spotlight pages, a Floor Guide, an information card, quick-access shortcuts, weather, search and filters, and an accessibility mode — in landscape or portrait.",
    prerequisites: [
      "The Wayfinding module on your organisation. Without it, Hotel Directory doesn’t appear in the store.",
    ],
    readingTime: 3,
    status: "draft",
    group: "Hotel",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find Hotel Directory in the Hotel category.",
          },
          {
            type: "note",
            tone: "info",
            text: "Hotel Directory is part of the paid Wayfinding module. If you don’t see it, ask your PulsePanels partner.",
          },
        ],
        screenshot: {
          src: "apps/hotel-directory/01.png",
          alt: "The app store with Hotel Directory in the Hotel group",
          todo: "Apps store, Hotel group, Hotel Directory card highlighted.",
        },
      },
      {
        title: "Set the header, search and accessibility",
        blocks: [
          {
            type: "p",
            text: "The editor opens with Directory type set to Hotel. Set the Title, You are here, and the QR label and QR link shown in the header. Under Header & footer choose what shows (logo, clock, QR code, weather, quick access, footer), and under Search & filters turn on the Search bar and the Category, Floor, Language and Sort A–Z filters.",
          },
        ],
        screenshot: {
          src: "apps/hotel-directory/02.png",
          alt: "The Directory editor set to Hotel with a live preview",
          todo: "Directory editor (Hotel) open, header + filters + live preview.",
        },
      },
      {
        title: "Add services, facilities and floors",
        blocks: [
          {
            type: "p",
            text: "Fill in Guest Services and Facilities. A facility can open its own spotlight page with images and details, and can have sub-items. Add your floors (+ Add floor) for the Floor Guide, then set the Information block, Weather, Quick Access items and Footer notices. Style the board with a logo, fonts, background and colours.",
          },
          {
            type: "note",
            tone: "tip",
            text: "Turn on Accessibility mode ON by default for a lower-reach layout, where search and filters move to a dock at the bottom.",
          },
        ],
        screenshot: {
          src: "apps/hotel-directory/03.png",
          alt: "Guest Services, Facilities and floor list in the Directory editor",
          caption: "Add services, facilities and floors, then style the board.",
          todo: "Directory editor (Hotel) scrolled to Guest Services / Facilities (one facility expanded with spotlight fields) and the floor list.",
        },
      },
      {
        title: "Save and assign to a screen",
        blocks: [
          {
            type: "p",
            text: "Name the directory, click Save Changes, and assign it to your lobby or corridor screen.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "Campus Directory", to: "/apps/campus-directory" },
      { label: "Hotel Welcome Board", to: "/apps/hotel-welcome" },
      { label: "Wayfinding", to: "/modules/wayfinding" },
    ],
    keywords: [
      "hotel directory",
      "guest directory",
      "guest services",
      "facilities",
      "floor guide",
      "accessibility",
      "touch",
    ],
  },

  {
    slug: "prayer-time",
    category: "apps",
    route: "/apps/prayer-time",
    title: "Prayer Time",
    summary: "Daily prayer times with a countdown, Hijri date, Iqama and optional Adhan.",
    intro:
      "The Prayer Time app shows the day’s prayer times for an exact location, a countdown to the next prayer and the Hijri date, in English, Arabic or both. Times come from AlAdhan and are cached a month at a time, so the screen keeps counting down even offline. It can also play the Adhan when a prayer time arrives.",
    readingTime: 3,
    status: "draft",
    group: "Hotel",
    lastUpdated: "2026-09-30",
    steps: [
      {
        title: "Open the App store",
        blocks: [
          {
            type: "p",
            text: "Click Apps in the sidebar, open the App Store tab, and find Prayer Time in the Hotel category.",
          },
        ],
        screenshot: {
          src: "apps/prayer-time/01.png",
          alt: "The app store with Prayer Time in the Hotel group",
          todo: "Apps store, Hotel group, Prayer Time card highlighted.",
        },
      },
      {
        title: "Set the location and calculation",
        blocks: [
          {
            type: "p",
            text: "Set the Title and an optional Subtitle / mosque. Under Location, search for an address or drop a pin on the map — times are computed for that exact point. Check the City label and Timezone, then choose the Calculation method your community follows and the Asr school (Shafi / Standard or Hanafi). Use Time adjustments to shift any prayer by a few minutes.",
          },
        ],
        screenshot: {
          src: "apps/prayer-time/02.png",
          alt: "The Prayer Time editor with the City label, Timezone, calculation method and a live preview",
          todo: "Prayer Time editor open for London (Europe/London, Muslim World League): City label, Timezone, Calculation method, Asr school + live preview. The local demo's Google Maps key refuses the helpdemo.localhost / localhost referrer (RefererNotAllowedMapError), so the location map is hidden; recapture on a host the key allows to show the map.",
        },
      },
      {
        title: "Choose Iqama, language, layout and Adhan",
        blocks: [
          {
            type: "p",
            text: "Turn on Show Iqama times if needed. Pick the Language (English, Arabic (RTL) or Bilingual), the Clock (12-hour or 24-hour), a Hijri offset, and the Layout — Auto (follow screen), Landscape, Portrait, Compact widget, or Mosque / Iqama. Keep the decorative background or upload your own.",
          },
          {
            type: "p",
            text: "To play the Adhan, tick Enable Adhan media at prayer time and choose the Playback mode — Audio only (keeps screen — recommended) or Video fullscreen — then upload the Adhan audio or video. You can use a different file for Fajr, choose which prayers play it, and set the Volume, Max playback and days to skip.",
          },
          {
            type: "note",
            tone: "info",
            text: "Sunrise never plays the Adhan. On a screen without speakers, set the Playback mode to Disabled.",
          },
        ],
        screenshot: {
          src: "apps/prayer-time/03.png",
          alt: "Iqama, Language, Layout and Adhan settings in the Prayer Time editor",
          caption: "Choose the layout and language, then set up the Adhan.",
          todo: "Prayer Time editor scrolled to Show Iqama times / Language / Layout / Enable Adhan media at prayer time with Playback mode.",
        },
      },
      {
        title: "Save and assign to a screen",
        blocks: [
          {
            type: "p",
            text: "Name the board, click Save Changes, and assign it to a screen.",
          },
          {
            type: "steps-ref",
            label: "See: Assign content to a screen",
            to: "/screens/assign-content",
          },
        ],
      },
    ],
    related: [
      { label: "How apps work", to: "/apps/add-an-app" },
      { label: "Hotel Welcome Board", to: "/apps/hotel-welcome" },
    ],
    keywords: [
      "prayer time",
      "adhan",
      "salah",
      "iqama",
      "hijri",
      "countdown",
      "aladhan",
      "calculation method",
      "offline",
    ],
  },
];
